const SETTINGS_KEY = 'priceDisplaySettings';

export const SUPPORTED_PRICE_CURRENCIES = ['EUR', 'CHF', 'GBP'];
export const EXCHANGE_TARGET_CURRENCIES = ['EUR', 'GBP', 'CHF', 'PLN', 'HUF', 'RUB', 'USD'];
export const PRICE_VIEW_MODES = {
  PRICES: 'prices',
  EXCHANGE: 'exchange'
};

const DEFAULT_SETTINGS = {
  viewMode: PRICE_VIEW_MODES.PRICES,
  selectedPriceCurrencies: [...SUPPORTED_PRICE_CURRENCIES],
  exchangeTargetCurrency: 'EUR'
};

const uniqueCurrencies = (values = []) => {
  const seen = new Set();
  return values
    .map((value) => String(value || '').trim().toUpperCase())
    .filter((value) => SUPPORTED_PRICE_CURRENCIES.includes(value))
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

export const sanitizePriceSettings = (rawSettings = {}) => {
  const selectedCurrenciesRaw = rawSettings.selectedPriceCurrencies || rawSettings.selectedCurrencies;
  const selectedCurrencies = uniqueCurrencies(selectedCurrenciesRaw);
  const safeSelectedCurrencies = selectedCurrencies.length > 0 ? selectedCurrencies : ['EUR'];

  if (safeSelectedCurrencies.length === 1 && safeSelectedCurrencies[0] !== 'EUR') {
    safeSelectedCurrencies.unshift('EUR');
  }

  let viewMode = rawSettings.viewMode;
  if (!viewMode) {
    const hasLegacyDisplayMode = rawSettings.displayMode === 'single' || rawSettings.displayMode === 'all';
    const legacyShowConverted = rawSettings.showConvertedPrices !== false;
    viewMode = hasLegacyDisplayMode && legacyShowConverted
      ? PRICE_VIEW_MODES.EXCHANGE
      : PRICE_VIEW_MODES.PRICES;
  }
  if (viewMode !== PRICE_VIEW_MODES.EXCHANGE) {
    viewMode = PRICE_VIEW_MODES.PRICES;
  }

  const exchangeTargetRaw = String(rawSettings.exchangeTargetCurrency || rawSettings.preferredCurrency || '').trim().toUpperCase();
  const exchangeTargetCurrency = EXCHANGE_TARGET_CURRENCIES.includes(exchangeTargetRaw)
    ? exchangeTargetRaw
    : 'EUR';

  return {
    viewMode,
    selectedPriceCurrencies: safeSelectedCurrencies,
    exchangeTargetCurrency
  };
};

export const getPriceSettings = () => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_SETTINGS };
  }

  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return sanitizePriceSettings({ ...DEFAULT_SETTINGS, ...parsed });
  } catch (_error) {
    return { ...DEFAULT_SETTINGS };
  }
};

export const savePriceSettings = (nextSettings) => {
  if (typeof window === 'undefined') return { ...DEFAULT_SETTINGS };

  const sanitized = sanitizePriceSettings({ ...DEFAULT_SETTINGS, ...nextSettings });
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(sanitized));
  return sanitized;
};

export const PRICE_SETTINGS_STORAGE_KEY = SETTINGS_KEY;
export const DEFAULT_PRICE_SETTINGS = DEFAULT_SETTINGS;
